import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useStaff } from '@/stores/staff';
import { useLocations } from '@/stores/locations';
import { useHasPayRates, useRole } from '@/stores/identity';
import { formatDateLong } from '@/lib/dates';
import { notify } from '@/lib/notify';
import {
  formatAsTyping,
  isValidNANP,
  PHONE_VALIDATION_HINT,
} from '@/lib/phoneNumber';
import { isValidEmail, suggestEmailFix } from '@/lib/emailValidation';
import { StaffStatusBadge } from '@/components/StaffStatusBadge';
import {
  createInvite,
  listInvites,
  resendInvite,
  revokeInvite,
  setStaffInactive,
  reactivateStaff,
  inviteAcceptUrl,
  promoteStaffToManager,
  demoteStaffToStaff,
  listManagerships,
  sendInviteEmail,
  type Managership,
  type InviteEmailResult,
} from '@/lib/staff';
import { ManagerBadge } from '@/components/ManagerBadge';
import {
  CrossOrgReadOnlyBanner,
  useIsReadOnlyCrossOrg,
} from '@/components/CrossOrgReadOnlyBanner';
import {
  EMPLOYMENT_TYPES,
  HOURS_TARGET_PERIODS,
  SELECTABLE_JOB_TYPES,
  type EmploymentType,
  type HoursTargetPeriod,
  type JobType,
  type StaffInvite,
  type Staff,
} from '@/lib/types';
import {
  CUSTOM_INITIALS_REGEX,
  displayInitialsForStaff,
  formatInitialsForChip,
  staffInitials,
} from '@/lib/scheduling';

interface StaffDetailRouteState {
  justCreatedInvite?: {
    url: string;
    expires_at: string;
    /** Optional: result of the auto-send email step performed in StaffNew */
    emailResult?: InviteEmailResult;
    /** The recipient email — used in the success message */
    recipientEmail?: string;
  };
  inviteError?: string;
}

export default function StaffDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const status = useStaff((s) => s.status);
  const items = useStaff((s) => s.items);
  const load = useStaff((s) => s.load);
  const update = useStaff((s) => s.update);
  const replace = useStaff((s) => s.replace);
  const role = useRole();
  const hasPayRates = useHasPayRates();

  // One-shot read of incoming route state from StaffNew's "Save and invite"
  // flow. After reading, clear it so a hard refresh doesn't resurface the
  // (sensitive) invite link or an old error.
  const incomingState = (location.state ?? null) as StaffDetailRouteState | null;
  const [justCreatedInvite, setJustCreatedInvite] = useState(
    incomingState?.justCreatedInvite ?? null
  );
  const [inviteError] = useState(incomingState?.inviteError ?? null);
  useEffect(() => {
    if (incomingState && (incomingState.justCreatedInvite || incomingState.inviteError)) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // We only want to run this on mount; subsequent renders shouldn't re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Managerships: who is manager at which location (org-scoped via RLS).
  // Drives the Manager badge in the header and the Promote/Demote panel.
  const [managerships, setManagerships] = useState<Managership[]>([]);
  const reloadManagerships = useCallback(async () => {
    try {
      const rows = await listManagerships();
      setManagerships(rows);
    } catch {
      // Silent: panel still renders, just without prefilled state.
    }
  }, []);
  useEffect(() => {
    void reloadManagerships();
  }, [reloadManagerships]);

  const locations = useLocations((s) => s.items);
  const locStatus = useLocations((s) => s.status);
  const loadLocations = useLocations((s) => s.load);

  const staff = items.find((s) => s.id === id) ?? null;

  // Hook must be called every render — pass the org id (or null while
  // loading). useIsReadOnlyCrossOrg handles null by returning false.
  const isReadOnlyCrossOrg = useIsReadOnlyCrossOrg(staff?.organization_id);

  useEffect(() => {
    if (status === 'idle') void load();
    if (locStatus === 'idle') void loadLocations();
  }, [status, locStatus, load, loadLocations]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading staff...
      </div>
    );
  }
  if (!staff) {
    return (
      <div className="container mx-auto py-10 px-4 text-center space-y-2">
        <h1 className="text-xl font-semibold">Staff not found</h1>
        <Link to="/staff" className="text-sm underline">
          Back to staff list
        </Link>
      </div>
    );
  }

  const isOwner = role === 'owner';
  const canEdit = role === 'owner' || role === 'manager';

  // Cross-org read-only: hook is called above pre-return. Hide write
  // surfaces so the UI tells the truth instead of failing after click.
  const showOwnerActions = isOwner && !isReadOnlyCrossOrg;
  const showEdit = canEdit && !isReadOnlyCrossOrg;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {isReadOnlyCrossOrg && (
          <CrossOrgReadOnlyBanner
            entityLabel="staff record"
            blockedActionsLabel="invites, promotions, and edits"
          />
        )}
        <Link to="/staff" className="text-sm text-muted-foreground underline">
          ← All staff
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {staff.display_name}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <StaffStatusBadge staff={staff} />
              {(() => {
                const m = staff.profile_id
                  ? managerships.find((mm) => mm.user_id === staff.profile_id)
                  : undefined;
                return m ? <ManagerBadge locationName={m.location_name} /> : null;
              })()}
              {!staff.profile_id && (
                showOwnerActions ? (
                  <a
                    href="#invite-panel"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById('invite-panel');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-200 transition-colors"
                    title="Click to send an invite"
                  >
                    No app access
                    <span aria-hidden="true">·</span>
                    <span className="underline">Send invite</span>
                  </a>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                    No app access
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        {showEdit ? (
          <DetailEditForm
            staff={staff}
            locations={locations}
            orgStaff={items}
            isOwner={isOwner}
            hasPayRates={hasPayRates}
            onSave={(patch) => update(staff.id, patch)}
          />
        ) : (
          <ReadOnlyView
            staff={staff}
            hasPayRates={hasPayRates}
            isReadOnlyCrossOrg={isReadOnlyCrossOrg}
          />
        )}

        {showOwnerActions && inviteError && (
          <div
            role="alert"
            className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            {inviteError}
          </div>
        )}

        {showOwnerActions && (
          <InvitePanel
            staffId={staff.id}
            staffEmail={staff.email}
            primaryLocationId={staff.primary_location_id}
            hasProfile={!!staff.profile_id}
            initialLink={
              justCreatedInvite
                ? {
                    url: justCreatedInvite.url,
                    expires_at: justCreatedInvite.expires_at,
                    emailResult: justCreatedInvite.emailResult,
                    recipientEmail: justCreatedInvite.recipientEmail,
                  }
                : null
            }
            onLinkConsumed={() => setJustCreatedInvite(null)}
          />
        )}

        {showOwnerActions && (
          <ManagementPanel
            staff={staff}
            locations={locations}
            managerships={managerships}
            onChanged={(updated) => {
              replace(updated);
              void reloadManagerships();
            }}
          />
        )}

        {showOwnerActions && (
          <LifecyclePanel
            staff={staff}
            onChanged={(updated) => replace(updated)}
          />
        )}

        <div className="text-xs text-muted-foreground">
          Created {new Date(staff.created_at).toLocaleString()} · Last updated{' '}
          {new Date(staff.updated_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

// ----- detail form ----------------------------------------------------------

function DetailEditForm({
  staff,
  locations,
  isOwner,
  hasPayRates,
  onSave,
  orgStaff,
}: {
  staff: Staff;
  locations: { id: string; name: string; status: string }[];
  isOwner: boolean;
  hasPayRates: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<Staff>;
  // Full org roster (active + inactive). Used to soft-warn on rename
  // collisions ("you're renaming this staff to a name another staff
  // already has"). Pure prop -- the parent already has the data.
  orgStaff: Staff[];
}) {
  // Split display_name into first/last on first space (best-effort heuristic
  // for legacy single-field records — saved back as a single space-joined name).
  const _initialFirst = (() => {
    const idx = (staff.display_name ?? '').indexOf(' ');
    return idx === -1 ? (staff.display_name ?? '') : staff.display_name.slice(0, idx);
  })();
  const _initialLast = (() => {
    const idx = (staff.display_name ?? '').indexOf(' ');
    return idx === -1 ? '' : staff.display_name.slice(idx + 1);
  })();
  const [firstName, setFirstName] = useState(_initialFirst);
  const [lastName, setLastName] = useState(_initialLast);
  const [email, setEmail] = useState(staff.email ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [phone, setPhone] = useState(formatAsTyping(staff.phone ?? ''));
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [address, setAddress] = useState(staff.address ?? '');
  const [emergencyName, setEmergencyName] = useState(
    staff.emergency_contact_name ?? ''
  );
  const [emergencyPhone, setEmergencyPhone] = useState(
    formatAsTyping(staff.emergency_contact_phone ?? '')
  );
  const [emergencyPhoneError, setEmergencyPhoneError] = useState<string | null>(null);
  const [jobType, setJobType] = useState<JobType>(staff.job_type);
  const [employmentType, setEmploymentType] = useState<EmploymentType>(
    staff.employment_type
  );
  const [primaryLocationId, setPrimaryLocationId] = useState(
    staff.primary_location_id ?? ''
  );
  const [minHours, setMinHours] = useState<number>(Number(staff.min_hours ?? 0));
  const [maxHours, setMaxHours] = useState<number>(Number(staff.max_hours ?? 40));
  const [hoursTargetPeriod, setHoursTargetPeriod] =
    useState<HoursTargetPeriod>(staff.hours_target_period ?? 'week');
  const [hourlyRate, setHourlyRate] = useState<string>(
    staff.hourly_rate != null ? String(staff.hourly_rate) : ''
  );
  // 0109: preferred display initials. Empty string = auto-derive from name.
  const _initialCustomInitials = staff.custom_initials ?? '';
  const [customInitials, setCustomInitials] = useState<string>(_initialCustomInitials);
  const [initialsError, setInitialsError] = useState<string | null>(null);
  // Migration 0096: active_from / active_to are plain editable fields.
  // Default to whatever is stored; user sees no extra clicks unless they
  // actually want a different date.
  const _initialActiveFrom = staff.active_from;
  const _initialActiveTo = staff.active_to;
  const [activeFrom, setActiveFrom] = useState<string>(staff.active_from);
  const [activeTo, setActiveTo] = useState<string>(staff.active_to ?? '');
  const todayStr = new Date().toISOString().slice(0, 10);

  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const trimmedEmail = email.trim();
      const trimmedPhone = phone.trim();
      const trimmedEmergencyPhone = emergencyPhone.trim();
      if (trimmedEmail && !isValidEmail(trimmedEmail)) {
        throw new Error('Please enter a valid email address before saving.');
      }
      if (trimmedPhone && !isValidNANP(trimmedPhone)) {
        throw new Error(`Phone: ${PHONE_VALIDATION_HINT}`);
      }
      if (trimmedEmergencyPhone && !isValidNANP(trimmedEmergencyPhone)) {
        throw new Error(`Emergency phone: ${PHONE_VALIDATION_HINT}`);
      }
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      if (!fullName) {
        throw new Error('Please enter a first and last name.');
      }

      // Soft duplicate-name warning on rename. Skips when the name is
      // unchanged (just other fields being edited). Excludes the current
      // staff row so resaving without a name change never trips it.
      const normalised = fullName.toLowerCase().replace(/\s+/g, ' ');
      const originalNormalised = (staff.display_name ?? '')
        .toLowerCase()
        .replace(/\s+/g, ' ');
      if (normalised !== originalNormalised) {
        const collision = orgStaff.find(
          (s) =>
            s.id !== staff.id &&
            (s.display_name ?? '').toLowerCase().replace(/\s+/g, ' ') === normalised
        );
        if (collision) {
          const ok = window.confirm(
            `Another staff member named "${collision.display_name}" already exists in your org. ` +
              `Rename anyway? (Click Cancel if you didn't mean to create a duplicate.)`
          );
          if (!ok) {
            setSubmitting(false);
            return;
          }
        }
      }

      const patch: Record<string, unknown> = {
        display_name: fullName,
        email: trimmedEmail || null,
        phone: trimmedPhone || null,
        address: address.trim() || null,
        emergency_contact_name: emergencyName.trim() || null,
        emergency_contact_phone: trimmedEmergencyPhone || null,
      };
      if (isOwner) {
        patch.job_type = jobType;
        patch.employment_type = employmentType;
        patch.primary_location_id = primaryLocationId || null;
      }
      if (maxHours < minHours) {
        throw new Error('Maximum hours must be greater than or equal to minimum.');
      }
      // Hour targets are editable by both owner and manager (server enforces).
      patch.min_hours = minHours;
      patch.max_hours = maxHours;
      patch.hours_target_period = hoursTargetPeriod;
      // Active dates (migration 0096). Only include keys that actually
      // changed -- keeps audit log clean when the user is editing
      // unrelated fields. Client-side range check; server enforces both
      // the range and the shift-conflict guard.
      const nextActiveTo = activeTo.trim() || null;
      if (nextActiveTo !== null && nextActiveTo < activeFrom) {
        throw new Error('End date must be on or after the start date.');
      }
      if (activeFrom !== _initialActiveFrom) {
        patch.active_from = activeFrom;
      }
      if (nextActiveTo !== _initialActiveTo) {
        patch.active_to = nextActiveTo;
      }
      // Pay rate: owner-only. Server also gates by org tier (pro/enterprise).
      // Only include in patch when the input value differs from what's stored —
      // avoids sending hourly_rate updates from orgs that don't have the feature.
      if (isOwner && hasPayRates) {
        const trimmed = hourlyRate.trim();
        const nextValue: number | null =
          trimmed === '' ? null : Number(trimmed);
        const currentValue = staff.hourly_rate ?? null;
        if (nextValue !== currentValue) {
          if (nextValue != null && (Number.isNaN(nextValue) || nextValue < 0)) {
            throw new Error('Hourly rate must be a non-negative number.');
          }
          patch.hourly_rate = nextValue;
        }
      }
      // 0109: custom initials. Editable by owner AND manager (server allows
      // both). Only include when changed. Empty -> null (clears back to
      // auto-derived). Client-side regex mirrors the CHECK constraint.
      const nextCustomInitials = customInitials.trim().toUpperCase();
      if (nextCustomInitials && !CUSTOM_INITIALS_REGEX.test(nextCustomInitials)) {
        throw new Error('Initials must be 1-4 uppercase letters (dots and hyphens allowed).');
      }
      if (nextCustomInitials !== (_initialCustomInitials || '').toUpperCase()) {
        patch.custom_initials = nextCustomInitials || null;
      }
      await onSave(patch);
      setSavedAt(new Date());
      // Group A convention (audit 2026-05-19): toast is the primary
      // "I did it" feedback; the "Last saved HH:MM" footnote stays as
      // the audit-trail signal.
      notify.success('Saved');
      // Group A convention (audit 2026-05-19): toast is the primary
      // "I did it" feedback; the "Last saved HH:MM" footnote stays as
      // the audit-trail signal.
      notify.success('Saved');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-border bg-card p-6 space-y-4"
    >
      <h2 className="text-base font-medium">Details</h2>

      <div className="grid grid-cols-2 gap-4">
        <Field label="First name" htmlFor="first_name">
          <input
            id="first_name"
            type="text"
            required
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Last name" htmlFor="last_name">
          <input
            id="last_name"
            type="text"
            required
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      <StaffInitialsField
        staffId={staff.id}
        firstName={firstName}
        lastName={lastName}
        value={customInitials}
        onChange={(v) => { setCustomInitials(v); if (initialsError) setInitialsError(null); }}
        error={initialsError}
        setError={setInitialsError}
        orgStaff={orgStaff}
      />

      <div className="grid grid-cols-2 gap-4">
        <Field label="Job type" htmlFor="job_type">
          <select
            id="job_type"
            value={jobType}
            disabled={!isOwner}
            onChange={(e) => setJobType(e.target.value as JobType)}
            className={inputClass}
          >
            {/* 'manager' is excluded from the picker (it's a role granted via
                Promote, not a job type). If a legacy record still carries it,
                show a disabled option so the select displays correctly and
                the owner is nudged to switch it to a real type. */}
            {jobType === 'manager' && (
              <option value="manager" disabled>
                Manager (legacy -- use Promote to manager)
              </option>
            )}
            {SELECTABLE_JOB_TYPES.map((j) => (
              <option key={j.value} value={j.value}>
                {j.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Employment type" htmlFor="employment_type">
          <select
            id="employment_type"
            value={employmentType}
            disabled={!isOwner}
            onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
            className={inputClass}
          >
            {EMPLOYMENT_TYPES.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field
        label="Primary location"
        htmlFor="primary_location"
        hint={isOwner ? undefined : 'Owner-only field.'}
      >
        <select
          id="primary_location"
          value={primaryLocationId}
          disabled={!isOwner}
          onChange={(e) => setPrimaryLocationId(e.target.value)}
          className={inputClass}
        >
          <option value="">— none —</option>
          {locations
            .filter((l) => l.status !== 'closed')
            .map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
        </select>
      </Field>

      {/* Migration 0096: active_from / active_to as plain editable fields.
          Defaults are the stored values — no extra clicks required if they
          are correct. Quick-action buttons on active_to set "today" or
          clear, but the actual write happens on Save. */}
      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Active from"
          htmlFor="active_from"
          hint="When this person's tenure starts."
        >
          <input
            id="active_from"
            type="date"
            value={activeFrom}
            onChange={(e) => setActiveFrom(e.target.value)}
            className={inputClass}
            required
          />
        </Field>
        <Field
          label="Active to"
          htmlFor="active_to"
          hint="Leave blank for ongoing employment."
        >
          <div className="flex items-center gap-2">
            <input
              id="active_to"
              type="date"
              value={activeTo}
              min={activeFrom}
              onChange={(e) => setActiveTo(e.target.value)}
              className={inputClass + ' flex-1'}
            />
            <button
              type="button"
              onClick={() => setActiveTo(todayStr)}
              className="shrink-0 text-xs px-2 py-1 rounded border border-border hover:bg-muted"
              title="Set to today"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setActiveTo('')}
              className="shrink-0 text-xs px-2 py-1 rounded border border-border hover:bg-muted"
              title="Clear end date"
              aria-label="Clear end date"
            >
              ×
            </button>
          </div>
        </Field>
      </div>

      <Field label="Email" htmlFor="email">
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
            if (emailSuggestion) setEmailSuggestion(null);
          }}
          onBlur={(e) => {
            const val = e.target.value.trim();
            if (val && !isValidEmail(val)) {
              setEmailError('Please enter a valid email address.');
              setEmailSuggestion(null);
            } else {
              setEmailError(null);
              setEmailSuggestion(suggestEmailFix(val));
            }
          }}
          className={inputClass}
        />
        {emailError && (
          <p className="text-xs text-destructive mt-1">{emailError}</p>
        )}
        {emailSuggestion && !emailError && (
          <p className="text-xs text-muted-foreground mt-1">
            Did you mean{' '}
            <button
              type="button"
              className="underline text-primary"
              onClick={() => {
                setEmail(emailSuggestion);
                setEmailSuggestion(null);
              }}
            >
              {emailSuggestion}
            </button>
            ?
          </p>
        )}
      </Field>
      <Field label="Phone" htmlFor="phone">
        <input
          id="phone"
          type="tel"
          value={phone}
          placeholder="(416) 555-1234"
          onChange={(e) => {
            setPhone(formatAsTyping(e.target.value));
            if (phoneError) setPhoneError(null);
          }}
          onBlur={(e) => {
            const val = e.target.value.trim();
            if (val && !isValidNANP(val)) {
              setPhoneError(PHONE_VALIDATION_HINT);
            } else {
              setPhoneError(null);
            }
          }}
          className={inputClass}
        />
        {phoneError && (
          <p className="text-xs text-destructive mt-1">{phoneError}</p>
        )}
      </Field>
      <Field label="Address" htmlFor="address">
        <input
          id="address"
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className={inputClass}
        />
      </Field>

      <fieldset className="rounded-md border border-border p-4 space-y-3">
        <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Expected hours
        </legend>
        <p className="text-xs text-muted-foreground">
          How many hours should this person be scheduled? Used to flag
          over- or under-utilization on the reports page. Defaults: 0
          minimum / 40 maximum per week.
        </p>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Minimum" htmlFor="min_hours">
            <input
              id="min_hours"
              type="number"
              min={0}
              step="0.5"
              value={minHours}
              onChange={(e) => setMinHours(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
          <Field label="Maximum" htmlFor="max_hours">
            <input
              id="max_hours"
              type="number"
              min={0}
              step="0.5"
              value={maxHours}
              onChange={(e) => setMaxHours(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
          <Field label="Period" htmlFor="hours_target_period">
            <select
              id="hours_target_period"
              value={hoursTargetPeriod}
              onChange={(e) =>
                setHoursTargetPeriod(e.target.value as HoursTargetPeriod)
              }
              className={inputClass}
            >
              {HOURS_TARGET_PERIODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </fieldset>

      {isOwner && (
        <fieldset className="rounded-md border border-border p-4 space-y-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Pay rate
          </legend>
          {hasPayRates ? (
            <>
              <p className="text-xs text-muted-foreground">
                Hourly rate in CAD. Visible only to owners. Drives the
                Labor cost report. Leave blank to clear.
              </p>
              <Field label="Hourly rate (CAD)" htmlFor="hourly_rate">
                <input
                  id="hourly_rate"
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 28.50 (CAD per hour)"
                />
              </Field>
            </>
          ) : (
            <div className="rounded-md border border-dashed border-border bg-muted/40 p-4 flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Pay rate is locked</span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium">
                    Pro plan
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Set hourly rates and unlock the Labor cost tile on the
                  Owner Reports page when you upgrade to the Pro or
                  Enterprise plan.
                </p>
                <Link
                  to="/billing"
                  className="inline-block text-xs font-medium text-primary hover:underline"
                >
                  Upgrade to Pro →
                </Link>
              </div>
            </div>
          )}
        </fieldset>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Emergency contact (name)" htmlFor="emergency_name">
          <input
            id="emergency_name"
            type="text"
            value={emergencyName}
            onChange={(e) => setEmergencyName(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Emergency contact (phone)" htmlFor="emergency_phone">
          <input
            id="emergency_phone"
            type="tel"
            value={emergencyPhone}
            placeholder="(416) 555-1234"
            onChange={(e) => {
              setEmergencyPhone(formatAsTyping(e.target.value));
              if (emergencyPhoneError) setEmergencyPhoneError(null);
            }}
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val && !isValidNANP(val)) {
                setEmergencyPhoneError(PHONE_VALIDATION_HINT);
              } else {
                setEmergencyPhoneError(null);
              }
            }}
            className={inputClass}
          />
          {emergencyPhoneError && (
            <p className="text-xs text-destructive mt-1">{emergencyPhoneError}</p>
          )}
        </Field>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}
      <div className="flex justify-end items-center gap-3">
        {savedAt && !error && (
          <span className="text-xs text-muted-foreground" aria-live="polite">
            Last saved {savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

function ReadOnlyView({
  staff,
  hasPayRates,
  isReadOnlyCrossOrg,
}: {
  staff: Staff;
  hasPayRates: boolean;
  isReadOnlyCrossOrg: boolean;
}) {
  // H-25 (QA suite): in cross-org admin view, PII fields (contact info +
  // compensation) are masked. Platform admins can confirm a record exists
  // and read operational fields (job type, employment type, hours), but
  // contact details and pay rate are private to the owning org. To access
  // them, the admin must sign in as that org's owner. Backend RLS still
  // returns the columns; this is a UI-layer mask. See ADR
  // docs/decisions/2026-06-14_cross_org_staff_pii_masking.md
  const hiddenValue: ReactNode = (
    <span
      className="italic text-muted-foreground"
      title="Hidden in cross-org admin view — sign in as this organization's owner to view"
    >
      Hidden
    </span>
  );
  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-2 text-sm">
      <Row
        label="Email"
        value={isReadOnlyCrossOrg ? hiddenValue : (staff.email ?? '—')}
      />
      <Row
        label="Phone"
        value={
          isReadOnlyCrossOrg
            ? hiddenValue
            : staff.phone
            ? formatAsTyping(staff.phone) || staff.phone
            : '—'
        }
      />
      <Row
        label="Address"
        value={isReadOnlyCrossOrg ? hiddenValue : (staff.address ?? '—')}
      />
      <Row label="Job type" value={staff.job_type} />
      <Row label="Employment type" value={staff.employment_type} />
      <Row
        label="Hour target"
        value={`${Number(staff.min_hours)}–${Number(staff.max_hours)} h / ${
          staff.hours_target_period === 'week' ? 'week' : 'month'
        }`}
      />
      {hasPayRates && !isReadOnlyCrossOrg && (
        <Row
          label="Hourly rate"
          value={
            staff.hourly_rate != null
              ? `$${Number(staff.hourly_rate).toFixed(2)} CAD/h`
              : '—'
          }
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border last:border-0 py-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

// ----- invite panel ---------------------------------------------------------

function InvitePanel({
  staffId,
  staffEmail,
  primaryLocationId,
  hasProfile,
  initialLink,
  onLinkConsumed,
}: {
  staffId: string;
  staffEmail: string | null;
  primaryLocationId: string | null;
  hasProfile: boolean;
  initialLink?:
    | {
        url: string;
        expires_at: string;
        emailResult?: InviteEmailResult;
        recipientEmail?: string;
      }
    | null;
  onLinkConsumed?: () => void;
}) {
  const [invites, setInvites] = useState<StaffInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New invite form. Invites always grant 'staff' role. Owners promote to
  // manager later via the Management panel below — this prevents accidental
  // manager grants at invite time and keeps the constraint check (1 manager
  // per location, must be a pharmacist) in a deliberate UI surface.
  const [email, setEmail] = useState(staffEmail ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastLink, setLastLink] = useState<{
    url: string;
    expires_at: string;
  } | null>(initialLink ? { url: initialLink.url, expires_at: initialLink.expires_at } : null);
  // Track auto-send status for the most recent invite shown in the green
  // box — either inherited from StaffNew via initialLink, or computed
  // locally after onCreate / onResend.
  const [emailStatus, setEmailStatus] = useState<
    'idle' | 'sending' | 'sent' | 'failed'
  >(
    initialLink?.emailResult
      ? initialLink.emailResult.sent
        ? 'sent'
        : 'failed'
      : 'idle'
  );
  const [emailStatusError, setEmailStatusError] = useState<string | null>(
    initialLink?.emailResult && !initialLink.emailResult.sent
      ? initialLink.emailResult.error
      : null
  );
  const [emailRecipient, setEmailRecipient] = useState<string>(
    initialLink?.recipientEmail ?? staffEmail ?? ''
  );

  // If the parent passes an initialLink (e.g. from the "Save and invite" flow
  // on StaffNew), notify it once so it can clear the route state.
  useEffect(() => {
    if (initialLink && onLinkConsumed) onLinkConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await listInvites();
      setInvites(all.filter((i) => i.staff_id === staffId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address before sending the invite.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await createInvite({
        staff_id: staffId,
        app_role: 'staff',
        email: trimmedEmail,
        location_ids: primaryLocationId ? [primaryLocationId] : [],
      });
      const url = inviteAcceptUrl(result.raw_token);
      setLastLink({ url, expires_at: result.expires_at });
      setEmailRecipient(trimmedEmail);
      setEmailStatus('sending');
      setEmailStatusError(null);
      const r = await sendInviteEmail(result.invite_id, result.raw_token);
      if (r.sent) {
        setEmailStatus('sent');
      } else {
        setEmailStatus('failed');
        setEmailStatusError(r.error);
      }
      await reload();
      notify.success(r.sent ? 'Invite created and emailed' : 'Invite created — email failed, link still works');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend(inviteId: string) {
    setError(null);
    try {
      const result = await resendInvite(inviteId);
      const url = inviteAcceptUrl(result.raw_token);
      setLastLink({ url, expires_at: result.expires_at });
      // Use the recipient on the existing invite row so the "sent to" message
      // matches what was actually sent.
      const inv = invites.find((i) => i.id === inviteId);
      if (inv?.email) setEmailRecipient(inv.email);
      setEmailStatus('sending');
      setEmailStatusError(null);
      const r = await sendInviteEmail(result.invite_id, result.raw_token);
      if (r.sent) {
        setEmailStatus('sent');
      } else {
        setEmailStatus('failed');
        setEmailStatusError(r.error);
      }
      await reload();
      notify.success(r.sent ? 'Invite resent and emailed' : 'New invite link ready — email failed');
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function onRevoke(inviteId: string) {
    const reason = prompt('Reason for revoking? (recorded in audit log)');
    if (!reason || !reason.trim()) return;
    try {
      await revokeInvite(inviteId, reason.trim());
      await reload();
      notify.info('Invite revoked');
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // older browsers
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }

  const pending = invites.filter((i) => i.status === 'pending');
  const history = invites.filter((i) => i.status !== 'pending');

  return (
    <section id="invite-panel" className="rounded-lg border border-border bg-card p-6 space-y-4 scroll-mt-20">
      <div className="space-y-1">
        <h2 className="text-base font-medium">App access</h2>
        <p className="text-xs text-muted-foreground">
          {hasProfile
            ? 'This staff member has an active account. Send a new invite only if their access needs to be re-established.'
            : 'Send an invite — we email the link to this person automatically, and also show it here so you can copy it for SMS or share it manually if the email fails.'}
        </p>
      </div>

      {!hasProfile && (
        <form
          onSubmit={onCreate}
          className="space-y-3 rounded-md border border-dashed border-border p-4"
        >
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="invite_email">
              Email
            </label>
            <input
              id="invite_email"
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
                if (emailSuggestion) setEmailSuggestion(null);
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val && !isValidEmail(val)) {
                  setEmailError('Please enter a valid email address.');
                  setEmailSuggestion(null);
                } else {
                  setEmailError(null);
                  setEmailSuggestion(suggestEmailFix(val));
                }
              }}
              className={inputClass}
            />
            {emailError && (
              <p className="text-xs text-destructive mt-1">{emailError}</p>
            )}
            {emailSuggestion && !emailError && (
              <p className="text-xs text-muted-foreground mt-1">
                Did you mean{' '}
                <button
                  type="button"
                  className="underline text-primary"
                  onClick={() => {
                    setEmail(emailSuggestion);
                    setEmailSuggestion(null);
                  }}
                >
                  {emailSuggestion}
                </button>
                ?
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Invite grants <strong>Staff</strong> role. Promote to manager from
              the Management panel below after they accept.
            </p>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create invite link'}
            </button>
          </div>
        </form>
      )}

      {lastLink && (
        <div
          className={
            emailStatus === 'failed'
              ? 'rounded-md border border-amber-300 bg-amber-50 p-4 space-y-2'
              : 'rounded-md border border-emerald-300 bg-emerald-50 p-4 space-y-2'
          }
        >
          <p
            className={
              emailStatus === 'failed'
                ? 'text-sm font-medium text-amber-900'
                : 'text-sm font-medium text-emerald-900'
            }
          >
            {emailStatus === 'sending' && 'Invite created — sending email...'}
            {emailStatus === 'sent' &&
              `Invite sent to ${emailRecipient || 'recipient'}`}
            {emailStatus === 'failed' &&
              'Invite created, but email send failed — copy and share manually'}
            {emailStatus === 'idle' && 'Invite link ready — copy and share'}
          </p>
          {emailStatus === 'failed' && emailStatusError && (
            <p className="text-xs text-amber-800">
              <strong>Reason:</strong> {emailStatusError}
            </p>
          )}
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={lastLink.url}
              onFocus={(e) => e.currentTarget.select()}
              className={
                emailStatus === 'failed'
                  ? 'flex-1 rounded-md border border-amber-300 bg-white px-3 py-2 text-xs font-mono'
                  : 'flex-1 rounded-md border border-emerald-300 bg-white px-3 py-2 text-xs font-mono'
              }
            />
            <button
              type="button"
              onClick={() => copyLink(lastLink.url)}
              className={
                emailStatus === 'failed'
                  ? 'rounded-md bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-700'
                  : 'rounded-md bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700'
              }
            >
              Copy
            </button>
          </div>
          <p
            className={
              emailStatus === 'failed'
                ? 'text-xs text-amber-800'
                : 'text-xs text-emerald-800'
            }
          >
            Expires {new Date(lastLink.expires_at).toLocaleString()}. The link
            won't be shown again — copy it now.
          </p>
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading invites...</p>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Pending invites
              </h3>
              {pending.map((i) => (
                <div
                  key={i.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border p-3 text-sm"
                >
                  <div className="space-y-0.5 min-w-0">
                    <p className="truncate">{i.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {i.app_role} · expires{' '}
                      {new Date(i.expires_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => onResend(i.id)}
                      className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/40"
                    >
                      Resend
                    </button>
                    <button
                      type="button"
                      onClick={() => onRevoke(i.id)}
                      className="rounded-md border border-destructive/40 text-destructive px-2 py-1 text-xs hover:bg-destructive/10"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {history.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">
                Past invites ({history.length})
              </summary>
              <ul className="mt-2 space-y-1">
                {history.map((i) => (
                  <li key={i.id} className="flex justify-between border-b border-border py-1">
                    <span>{i.email}</span>
                    <span className="text-muted-foreground">
                      {i.status} · {new Date(i.updated_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}

// ----- lifecycle panel ------------------------------------------------------

function LifecyclePanel({
  staff,
  onChanged,
}: {
  staff: Staff;
  onChanged: (s: Staff) => void;
}) {
  const isInactive =
    staff.active_to !== null &&
    staff.active_to <= new Date().toISOString().slice(0, 10);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDeactivate() {
    const reason = prompt('Reason for deactivation? (recorded in audit log)');
    if (!reason || !reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await setStaffInactive(
        staff.id,
        new Date().toISOString().slice(0, 10),
        reason.trim()
      );
      onChanged(updated);
      notify.success('Marked inactive');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onReactivate() {
    const reason = prompt('Reason for reactivation? (recorded in audit log)');
    if (!reason || !reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await reactivateStaff(
        staff.id,
        new Date().toISOString().slice(0, 10),
        reason.trim()
      );
      onChanged(updated);
      notify.success('Reactivated');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-3">
      <h2 className="text-base font-medium">Employment status</h2>
      <p className="text-xs text-muted-foreground">
        {isInactive
          ? `Inactive since ${formatDateLong(staff.active_to)}.`
          : 'Currently active.'}
      </p>
      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </p>
      )}
      <div>
        {isInactive ? (
          <button
            type="button"
            onClick={onReactivate}
            disabled={submitting}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50"
          >
            {submitting ? 'Working...' : 'Reactivate'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onDeactivate}
            disabled={submitting}
            className="rounded-md border border-destructive/40 text-destructive px-3 py-1.5 text-sm hover:bg-destructive/10 disabled:opacity-50"
          >
            {submitting ? 'Working...' : 'Deactivate'}
          </button>
        )}
      </div>
    </section>
  );
}

// ----- shared ---------------------------------------------------------------

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

// ----- management panel (promote / demote) -----------------------------------

function ManagementPanel({
  staff,
  locations,
  managerships,
  onChanged,
}: {
  staff: Staff;
  locations: { id: string; name: string; status: string }[];
  managerships: Managership[];
  onChanged: (updated: Staff) => void;
}) {
  const myManagership = staff.profile_id
    ? managerships.find((m) => m.user_id === staff.profile_id) ?? null
    : null;
  const isManager = !!myManagership;

  const isPharmacist = staff.job_type === 'pharmacist';
  const hasProfile = !!staff.profile_id;
  const isActive = !staff.active_to || staff.active_to > new Date().toISOString().slice(0, 10);

  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    staff.primary_location_id ?? ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-flight: if this is the promote path, warn when the chosen location
  // already has a different manager. The RPC will refuse, but it's nicer to
  // tell the owner up front.
  const conflict = !isManager && selectedLocationId
    ? managerships.find(
        (m) =>
          m.location_id === selectedLocationId &&
          m.user_id !== staff.profile_id
      ) ?? null
    : null;

  async function onPromote() {
    if (!selectedLocationId) {
      setError('Pick a location for this manager.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const updated = await promoteStaffToManager(staff.id, selectedLocationId);
      onChanged(updated);
      notify.success('Promoted to manager');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDemote() {
    const locName = myManagership?.location_name ?? 'their location';
    const ok = window.confirm(
      `Demote ${staff.display_name} from manager at ${locName}? ` +
        `They'll keep app access as a regular staff member.`
    );
    if (!ok) return;
    setError(null);
    setSubmitting(true);
    try {
      const updated = await demoteStaffToStaff(staff.id);
      onChanged(updated);
      notify.success('Demoted to staff');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-medium">Pharmacy management</h2>
        <p className="text-xs text-muted-foreground">
          Promote a pharmacist to manage a location, or demote them back to a
          regular staff member. One manager per location; the manager must be
          a pharmacist.
        </p>
      </div>

      {!hasProfile && (
        <div className="rounded-md border border-dashed border-border bg-muted/40 p-4 text-xs text-muted-foreground">
          This staff member has not accepted an invite yet, so they can't be
          promoted. Send an invite from the App access section above first.
        </div>
      )}

      {hasProfile && !isActive && (
        <div className="rounded-md border border-dashed border-border bg-muted/40 p-4 text-xs text-muted-foreground">
          This staff record is inactive. Reactivate it before changing
          management.
        </div>
      )}

      {hasProfile && isActive && isManager && (
        <div className="space-y-3">
          <p className="text-sm">
            Currently managing{' '}
            <strong>{myManagership?.location_name}</strong>.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onDemote}
              disabled={submitting}
              className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              {submitting ? 'Demoting...' : 'Demote to staff'}
            </button>
          </div>
        </div>
      )}

      {hasProfile && isActive && !isManager && (
        <div className="space-y-3">
          {!isPharmacist && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>Job type must be pharmacist.</strong> This staff is{' '}
              <em>{staff.job_type}</em>. Edit the staff record above and change
              the job type before promoting.
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="promote_location">
              Location to manage
            </label>
            <select
              id="promote_location"
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className={inputClass}
              disabled={!isPharmacist}
            >
              <option value="">— pick a location —</option>
              {locations
                .filter((l) => l.status !== 'closed')
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </div>

          {conflict && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>{conflict.location_name}</strong> already has a manager.
              Open the current manager's staff page and demote them first, then
              try again.
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onPromote}
              disabled={
                submitting || !isPharmacist || !selectedLocationId || !!conflict
              }
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Promoting...' : 'Promote to manager'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * 0109: staff detail-page initials editor. Same UX as StaffNew.InitialsField
 * but excludes the current staff row from the org-scoped collision check
 * (a staff member's own record shouldn't collide with itself).
 */
function StaffInitialsField({
  staffId,
  firstName,
  lastName,
  value,
  onChange,
  error,
  setError,
  orgStaff,
}: {
  staffId: string;
  firstName: string;
  lastName: string;
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  setError: (s: string | null) => void;
  orgStaff: { id: string; display_name: string; custom_initials?: string | null }[];
}) {
  const trimmed = value.trim().toUpperCase();
  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
  const effective = trimmed
    ? trimmed
    : fullName
      ? staffInitials(fullName)
      : '';
  const chipRender = effective ? formatInitialsForChip(effective) : '';
  const collisions = effective
    ? orgStaff.filter(
        (s) =>
          s.id !== staffId &&
          displayInitialsForStaff(s).toUpperCase() === effective.toUpperCase()
      )
    : [];

  return (
    <div className="rounded-md border border-border p-4 space-y-2">
      <label htmlFor="custom_initials" className="block text-sm font-medium">
        Preferred initials (optional)
      </label>
      <p className="text-xs text-muted-foreground">
        Shown on the calendar shift chip and the sidebar avatar. Leave blank to
        auto-derive from the name. 1&ndash;4 uppercase letters; dots and hyphens
        allowed.
      </p>
      <div className="flex items-center gap-3">
        <input
          id="custom_initials"
          type="text"
          maxLength={4}
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onBlur={() => {
            const v = value.trim().toUpperCase();
            if (v && !CUSTOM_INITIALS_REGEX.test(v)) {
              setError('1-4 uppercase letters, dots, or hyphens.');
            } else {
              setError(null);
            }
          }}
          placeholder={fullName ? staffInitials(fullName) : 'e.g. SAK'}
          className={inputClass + ' font-mono uppercase'}
          style={{ maxWidth: '160px' }}
          autoComplete="off"
        />
        {chipRender && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Preview:</span>
            <span className="inline-block rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-sm tabular-nums">
              {chipRender}
            </span>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {!error && collisions.length > 0 && (
        <p className="text-xs text-amber-700">
          <strong>Heads up:</strong> {collisions.length === 1
            ? `${collisions[0].display_name} already uses these initials.`
            : `${collisions[0].display_name} and ${collisions.length - 1} other${collisions.length === 2 ? '' : 's'} already use these initials.`}{' '}
          Save is allowed &mdash; consider a distinct value to avoid confusion.
        </p>
      )}
    </div>
  );
}